import { MarketplaceSchedule } from '@spike/model';
import { DatePicker } from 'components/UI/V3/DatePicker/DatePicker';
import {
    getBoardingNights,
    getWaitlistOpenDates,
    normalizeWaitlistDates
} from 'components/Waitlist/WaitlistForm/waitlistFormUtils';
import { formatWaitlistServiceDates } from 'components/Waitlist/waitlistUtils';
import moment, { Moment } from 'moment-timezone';
import { FunctionComponent, useMemo, useState } from 'react';
import calendarStyles from './WaitlistCalendar.module.scss';
import styles from './WaitlistServiceDatesField.module.scss';
import WaitlistPickerField from './WaitlistPickerField';

interface WaitlistServiceDatesFieldProps {
    businessArea: string;
    /** "YYYY-MM-DD"; boarding keeps the nights. [] = ASAP. */
    dates: Array<string>;
    timeZone: string;
    schedule?: MarketplaceSchedule;
    onChange: (dates: Array<string>) => void;
}

const DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Requested dates of one service, one per occurrence: the appointment day, each daycare day,
 * or each boarding night (picked as check-in → check-out).
 */
export const WaitlistServiceDatesField: FunctionComponent<WaitlistServiceDatesFieldProps> = ({
    businessArea,
    dates,
    timeZone,
    schedule,
    onChange
}) => {
    const isBoarding = businessArea === 'boarding';
    const isDaycare = businessArea === 'daycare';

    const [option, setOption] = useState(dates.length > 0 ? 'dates' : 'asap');
    // Boarding check-in clicked, waiting for the check-out.
    const [checkIn, setCheckIn] = useState<Moment>();

    const today = moment().tz(timeZone).startOf('day');

    // Days the location is open (next 12 months). Boarding nights can include closed days.
    const openDates = useMemo(
        () => (isBoarding ? undefined : getWaitlistOpenDates(schedule, moment().tz(timeZone))),
        [schedule, timeZone, isBoarding]
    );

    const toMoment = (date: string) => moment.tz(date, DATE_FORMAT, timeZone);

    // Boarding shows the nights plus the check-out day.
    const selectedDays = useMemo(() => {
        if (checkIn) {
            return [checkIn];
        }

        const days = dates.map(toMoment);

        return isBoarding && days.length > 0 ? [...days, days[days.length - 1].clone().add(1, 'day')] : days;
    }, [dates, checkIn, isBoarding, timeZone]);

    const changeBoarding = (clicked: Moment, close: () => void) => {
        if (checkIn && clicked.isAfter(checkIn, 'day')) {
            onChange(getBoardingNights(checkIn, clicked));
            setCheckIn(undefined);
            close();
            return;
        }

        setCheckIn(clicked);
    };

    const changeDays = (days: Array<Moment>, close: () => void) => {
        const previous = new Set(selectedDays.map(day => day.format(DATE_FORMAT)));
        const next = new Set(days.map(day => day.format(DATE_FORMAT)));
        // The day that was clicked: the one added or removed.
        const clicked =
            days.find(day => !previous.has(day.format(DATE_FORMAT))) ??
            selectedDays.find(day => !next.has(day.format(DATE_FORMAT)));

        if (!clicked) {
            return;
        }

        if (isBoarding) {
            changeBoarding(clicked, close);
            return;
        }

        if (isDaycare) {
            onChange(normalizeWaitlistDates(days.map(day => day.format(DATE_FORMAT))));
            return;
        }

        onChange([clicked.format(DATE_FORMAT)]);
        close();
    };

    return (
        <WaitlistPickerField
            label="Requested Date"
            value={formatWaitlistServiceDates(dates, businessArea)}
            options={[
                { id: 'asap', label: 'ASAP' },
                { id: 'dates', label: isBoarding || isDaycare ? 'Select Dates' : 'Select Date' }
            ]}
            activeOption={option}
            onSelectOption={(selected, close) => {
                setOption(selected);
                setCheckIn(undefined);

                if (selected === 'asap') {
                    onChange([]);
                    close();
                }
            }}
            renderPicker={close => (
                <div className={styles.picker}>
                    <DatePicker
                        multipleMode
                        className={calendarStyles.calendar}
                        timeZone={timeZone}
                        minDate={today}
                        allowedDates={openDates}
                        value={selectedDays}
                        onChange={days => changeDays(days, close)}
                    />
                </div>
            )}
        />
    );
};

export default WaitlistServiceDatesField;
